import React, { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useAppOutletContext } from './AppOutletContext';

export interface AppNavigationEntry {
    id?: string;
    breadcrumb?: Breadcrumb;
    navigation?: NavigationRenderFunction;
    applicationTitle?: string;
}

export type Breadcrumb = {
    path?: string;
    name: ReactNode | (() => Promise<ReactNode>);
};
export type NavigationRenderFunction = (outlet: ReactNode) => ReactNode;

interface AppNavigationContextValue {
    breadcrumbs: Breadcrumb[];
    navigation: ReactNode;
    applicationTitle?: string;
    insert(index: number, entry: AppNavigationEntry): void;
    remove(index: number): void;
}

const AppNavigationContext = createContext<AppNavigationContextValue | undefined>(undefined);

export interface AppNavigationContextProviderProps extends PropsWithChildren<any> {
    defaultApplicationTitle?: string;
}

/**
 * Context used to store hierarchical navigation context like side nav content and breadcrumbs
 */
export function AppNavigationContextProvider(props: AppNavigationContextProviderProps): React.JSX.Element {
    const { defaultApplicationTitle, children } = props;

    const entriesRef = React.useRef<AppNavigationEntry[]>([]);

    // Using useState for the entries presented problems diff checking,
    // this improves the necessary render count to achieve an updated display
    const [renderCount, setRenderCount] = useState(0);
    const renderCountRef = React.useRef(renderCount);
    renderCountRef.current = renderCount;

    const contextValue = useMemo(() => {
        const insert = (depth: number, entry: AppNavigationEntry) => {
            entriesRef.current[depth] = {
                ...(entriesRef.current[depth] || {}),
                ...entry,
            };
            setRenderCount(renderCountRef.current + 1);
        };

        const remove = (depth: number) => {
            entriesRef.current.length = depth;
            setRenderCount(renderCountRef.current + 1);
        };

        const entries = entriesRef.current;

        const breadcrumbs = entries.reduce((breadcrumbs, entry) => {
            if (entry?.breadcrumb != null) {
                breadcrumbs.push(entry.breadcrumb);
            }
            return breadcrumbs;
        }, [] as Breadcrumb[]);

        let navigation: ReactNode | undefined = undefined;
        for (let i = entries.length - 1; i >= 0; i--) {
            const entry = entries[i];
            if (entry?.navigation != null) {
                navigation = entry.navigation(navigation);
            }
        }

        const applicationTitle =
            entriesRef.current.findLast(x => x?.applicationTitle != null)?.applicationTitle || defaultApplicationTitle;

        return {
            breadcrumbs,
            navigation,
            applicationTitle,
            insert,
            remove,
        };
    }, [renderCount]);

    // Apply a document title when there are no breadcrumbs
    // This title should typically the application or product name
    const { breadcrumbs, applicationTitle } = contextValue;
    useEffect(() => {
        if (breadcrumbs.length === 0 && applicationTitle != null) {
            document.title = applicationTitle;
        }
    }, [breadcrumbs.length, applicationTitle]);

    return <AppNavigationContext.Provider value={contextValue}>{children}</AppNavigationContext.Provider>;
}

function useAppNavigationContext(): AppNavigationContextValue {
    const context = useContext(AppNavigationContext);
    if (context == null) {
        throw new Error(`useAppNavigationContext() must be used within a <AppNavigationContextProvider/>`);
    }
    return context;
}

/**
 * Call this hook to get the breadcrumb content to render
 */
export function useAppBreadcrumbs(): Breadcrumb[] {
    const navigationContext = useAppNavigationContext();
    return navigationContext.breadcrumbs;
}

/**
 * Call this hook to get the side nav content to render
 */
export function useAppNavigation(): ReactNode {
    const navigationContext = useAppNavigationContext();
    return navigationContext.navigation;
}

/**
 * Call this hook to register navigation content for the side nav or breadcrumbs
 *
 * @param entry The navigation content to register
 * @param dependencies Any dependencies that when changed, should cause the content to re-render
 */
export function useAppNavigationContent(entry: AppNavigationEntry, dependencies: any[] = []): void {
    const navigationContext = useAppNavigationContext();
    const outletContext = useAppOutletContext();

    useEffect(() => {
        navigationContext.insert(outletContext.depth, entry);
    }, dependencies);

    useEffect(() => {
        return () => {
            navigationContext.remove(outletContext.depth);
        };
    }, []);
}
